import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Search } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductGroupsQuery,
  useLazySearchProductsAllBranchesQuery,
} from "../productsApi";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useClaims } from "../../../auth/useClaims";
import { useCanCreateProducts, useCanEditProducts } from "../useProductPermissions";
import LinkedProductsPanel from "../components/LinkedProductsPanel";
import BranchAvailabilityPanel from "../components/BranchAvailabilityPanel";
import { paths } from "../../../../routes/paths";

const PRODUCT_TYPES = ["Item", "Kit Item"];
const ISSUE_TO_OPTIONS = ["Customers", "Employees", "Both"];

const emptyForm = {
  branchId: "",
  isActive: true,
  productCodeNew: "",
  productName: "",
  productGroupId: "",
  productType: "Item",
  unit: "",
  alterUnit: "",
  purchaseRate: "0",
  markupPercentage: "0",
  minimum: "0",
  reOrder: "0",
  maximum: "0",
  description: "",
  vat: "0",
  issueTo: "Both",
};

// Mirrors texas_master's Frm_Product_Details "Item" tab, against the live
// /api/v1/masters/products endpoints (handoff §9.8) - same branch-wise
// pattern as Customer (§9.7): Product_Code collides across branches, so
// every edit route needs {branchId}. salesRate is computed server-side
// (purchaseRate + purchaseRate*markupPercentage/100) - shown as a live
// client-side preview but never sent in the save body.
const ProductForm = () => {
  const { branchId: routeBranchId, productCode } = useParams();
  const navigate = useNavigate();
  const canCreate = useCanCreateProducts();
  const canEdit = useCanEditProducts();
  const isEditing = Boolean(productCode);
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: allGroups = [] } = useGetProductGroupsQuery();
  const {
    data: detail,
    isLoading: isLoadingDetail,
    isError: isDetailError,
    error: detailError,
  } = useGetProductQuery(
    { branchId: routeBranchId, productCode },
    { skip: !isEditing }
  );
  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  const [triggerDuplicateSearch] = useLazySearchProductsAllBranchesQuery();
  // Live typeahead (as-you-type, debounced) under the Name field - purely
  // informational, links open in a new tab so the in-progress form isn't
  // lost. The search button pins the same query's results into a
  // persistent card box at the bottom, styled like "Linked in Other
  // Branches" (handoff §9.8/§13.2), for a fuller look than the dropdown.
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  const typeaheadTimer = useRef(null);

  const availableBranches = isAllBranches
    ? branches
    : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (isEditing && detail) {
      setForm({
        ...emptyForm,
        ...detail,
        unit: detail.unit ?? "",
        alterUnit: detail.alterUnit ?? "",
        purchaseRate: String(detail.purchaseRate ?? "0"),
        markupPercentage: String(detail.markupPercentage ?? "0"),
        minimum: String(detail.minimum ?? "0"),
        reOrder: String(detail.reOrder ?? "0"),
        maximum: String(detail.maximum ?? "0"),
        vat: String(detail.vat ?? "0"),
      });
    } else if (!isEditing) {
      setForm({
        ...emptyForm,
        branchId: availableBranches.length === 1 ? availableBranches[0].branchId : "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, detail]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const branchGroups = allGroups.filter((g) => g.branchId === Number(form.branchId));
  const salesRatePreview =
    (Number(form.purchaseRate) || 0) +
    (Number(form.purchaseRate) || 0) * ((Number(form.markupPercentage) || 0) / 100);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, productName: value }));
    if (isEditing) return;
    if (typeaheadTimer.current) clearTimeout(typeaheadTimer.current);
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setTypeaheadResults([]);
      setTypeaheadOpen(false);
      return;
    }
    typeaheadTimer.current = setTimeout(async () => {
      const { data = [] } = await triggerDuplicateSearch(trimmed);
      setTypeaheadResults(data);
      setTypeaheadOpen(data.length > 0);
    }, 300);
  };

  // Duplicate-detection suggestion (handoff §9.8/§13.2) - non-blocking,
  // explicit (button click, not a hidden blur trigger). Never stops the
  // save; just flags possible existing products in other branches so
  // staff can link them afterward instead of accidentally duplicating.
  const handleSearchDuplicates = async () => {
    const name = form.productName.trim();
    if (name.length < 2) {
      toast.error("Type at least 2 characters of the name first");
      return;
    }
    setIsSearchingDuplicates(true);
    try {
      const { data = [] } = await triggerDuplicateSearch(name);
      setDuplicateMatches(data);
      setTypeaheadOpen(false);
      if (data.length === 0) toast.success("No matching names found in other branches");
    } finally {
      setIsSearchingDuplicates(false);
    }
  };

  const handleSave = async () => {
    if (!form.productCodeNew.trim()) {
      toast.error("Product code is required");
      return;
    }
    if (!form.productName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!isEditing && !form.branchId) {
      toast.error("Branch is required");
      return;
    }
    const body = {
      branchId: Number(form.branchId),
      productCodeNew: form.productCodeNew.trim(),
      productName: form.productName.trim(),
      productGroupId: form.productGroupId ? Number(form.productGroupId) : null,
      productType: form.productType,
      unit: form.unit === "" ? null : Number(form.unit),
      alterUnit: form.alterUnit === "" ? null : Number(form.alterUnit),
      purchaseRate: Number(form.purchaseRate) || 0,
      markupPercentage: Number(form.markupPercentage) || 0,
      minimum: Number(form.minimum) || 0,
      reOrder: Number(form.reOrder) || 0,
      maximum: Number(form.maximum) || 0,
      description: form.description || "",
      vat: Number(form.vat) || 0,
      issueTo: form.issueTo,
      isActive: form.isActive,
    };
    try {
      if (isEditing) {
        await updateProduct({ branchId: routeBranchId, productCode, ...body }).unwrap();
        toast.success("Product saved");
        navigate(paths.productEdit(routeBranchId, productCode), { replace: true });
      } else {
        const created = await createProduct(body).unwrap();
        toast.success("Product saved");
        navigate(paths.productEdit(created.branchId, created.productCode), { replace: true });
      }
    } catch (error) {
      toast.error(error?.message || "Could not save product");
    }
  };

  if (isEditing && isDetailError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.products} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Products
          </Link>
          <div className="alert alert-danger">
            Could not load this product ({detailError?.message || "server error"}). Try again,
            or report branch {routeBranchId} / productCode {productCode} to the API team.
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && isLoadingDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Products.Create/.Edit are separate permissions (§13.3) - creating a new
  // product needs Create, editing an existing one needs Edit.
  const readOnly = isEditing ? !canEdit : !canCreate;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header flex-wrap">
          <div className="add-item d-flex" style={{ minWidth: 0 }}>
            <div className="page-title" style={{ maxWidth: 700, wordBreak: "break-word" }}>
              <Link to={paths.products} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Products
              </Link>
              <h4>{isEditing ? form.productName || "Product" : "Add New Product"}</h4>
              <h6>Code: {isEditing ? form.productCodeNew || productCode : "assigned on save"}</h6>
            </div>
          </div>
        </div>

        {readOnly && (
          <div className="alert alert-info">
            You have read-only access. {isEditing ? "Editing" : "Creating"} products requires the
            Products.{isEditing ? "Edit" : "Create"} permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row">
              <div className="col-12 mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={readOnly}
                  />
                  <label className="form-check-label" htmlFor="isActive">Active</label>
                </div>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Branch</label>
                {isEditing ? (
                  <input
                    type="text"
                    className="form-control"
                    value={form.branchCode || branches.find((b) => b.branchId === form.branchId)?.branchCode || ""}
                    readOnly
                    disabled
                  />
                ) : (
                  <select
                    className="form-control"
                    value={form.branchId}
                    onChange={setField("branchId")}
                    disabled={readOnly}
                  >
                    <option value="">--Select--</option>
                    {availableBranches.map((b) => (
                      <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Product Code</label>
                <input type="text" className="form-control" value={form.productCodeNew} onChange={setField("productCodeNew")} disabled={readOnly} />
              </div>
              <div className="col-md-6 mb-3" style={{ position: "relative" }}>
                <label className="form-label">Name</label>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    value={form.productName}
                    onChange={handleNameChange}
                    onFocus={() => setTypeaheadOpen(typeaheadResults.length > 0)}
                    onBlur={() => setTimeout(() => setTypeaheadOpen(false), 150)}
                    disabled={readOnly}
                    autoComplete="off"
                  />
                  {!isEditing && (
                    <button
                      type="button"
                      className="btn btn-outline-primary flex-shrink-0"
                      onClick={handleSearchDuplicates}
                      disabled={readOnly || isSearchingDuplicates}
                      title="Search this name across all branches"
                    >
                      <Search size={16} />
                    </button>
                  )}
                </div>
                {typeaheadOpen && typeaheadResults.length > 0 && (
                  <div
                    className="card position-absolute w-100 mt-1"
                    style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
                  >
                    <div className="list-group list-group-flush">
                      {typeaheadResults.map((m) => (
                        <Link
                          key={`${m.branchId}-${m.productCode}`}
                          to={paths.productEdit(m.branchId, m.productCode)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="list-group-item list-group-item-action py-2"
                        >
                          <div className="fw-semibold">{m.productName}</div>
                          <div className="text-muted small">{m.branchCode} · {m.productCodeNew}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Group</label>
                <select className="form-control" value={form.productGroupId} onChange={setField("productGroupId")} disabled={readOnly || !form.branchId}>
                  <option value="">--Select--</option>
                  {branchGroups.map((g) => (
                    <option key={g.groupId} value={g.groupId}>{g.groupName}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Product Type</label>
                <select className="form-control" value={form.productType} onChange={setField("productType")} disabled={readOnly}>
                  {PRODUCT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Unit</label>
                <input type="number" className="form-control" value={form.unit} onChange={setField("unit")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Alter Unit</label>
                <input type="number" className="form-control" value={form.alterUnit} onChange={setField("alterUnit")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">VAT %</label>
                <input type="number" className="form-control" value={form.vat} onChange={setField("vat")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Purchase Rate</label>
                <input type="number" className="form-control" value={form.purchaseRate} onChange={setField("purchaseRate")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Markup %</label>
                <input type="number" className="form-control" value={form.markupPercentage} onChange={setField("markupPercentage")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Sales Rate</label>
                <input type="text" className="form-control bg-light" value={salesRatePreview.toFixed(2)} readOnly disabled />
                <small className="text-muted">Computed server-side on save.</small>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Minimum</label>
                <input type="number" className="form-control" value={form.minimum} onChange={setField("minimum")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Re-Order</label>
                <input type="number" className="form-control" value={form.reOrder} onChange={setField("reOrder")} disabled={readOnly} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Maximum</label>
                <input type="number" className="form-control" value={form.maximum} onChange={setField("maximum")} disabled={readOnly} />
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label d-block">Issue To</label>
                <div className="d-flex gap-4">
                  {ISSUE_TO_OPTIONS.map((opt) => (
                    <div className="form-check" key={opt}>
                      <input
                        type="radio"
                        className="form-check-input"
                        id={`issueTo-${opt}`}
                        name="issueTo"
                        checked={form.issueTo === opt}
                        onChange={() => setForm((prev) => ({ ...prev, issueTo: opt }))}
                        disabled={readOnly}
                      />
                      <label className="form-check-label" htmlFor={`issueTo-${opt}`}>{opt}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-12 mb-3">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description} onChange={setField("description")} disabled={readOnly} />
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isCreating || isUpdating}>
                  Save
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.products)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {!isEditing && duplicateMatches.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Possible Matches in Other Branches</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Dismiss"
                  onClick={() => setDuplicateMatches([])}
                />
              </div>
              <p className="text-muted small">
                These look similar to what you're creating - not necessarily the same product.
                Review, and if it really is the same one, save this record first, then use "Linked
                in Other Branches" on the new record to link them.
              </p>
              <div className="row">
                {duplicateMatches.map((m) => (
                  <div className="col-md-6 mb-2" key={`${m.branchId}-${m.productCode}`}>
                    <div className="border rounded p-2">
                      <Link to={paths.productEdit(m.branchId, m.productCode)} target="_blank" rel="noopener noreferrer">
                        <strong>{m.productName}</strong>
                      </Link>
                      <div className="text-muted small">{m.branchCode} · {m.productCodeNew}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {isEditing && (
          <BranchAvailabilityPanel
            branchId={routeBranchId}
            productCode={productCode}
            homeBranchCode={form.branchCode}
            readOnly={readOnly}
          />
        )}

        {isEditing && (
          <LinkedProductsPanel branchId={routeBranchId} productCode={productCode} readOnly={readOnly} />
        )}
      </div>
    </div>
  );
};

export default ProductForm;
